import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProduccionMensual } from './useProduccionMensual';

// --- COMPONENTE SKELETON LOADER CON ANIMACIÓN PULSANTE ---
const WidgetProduccionSkeleton = () => (
    <div className="space-y-3 animate-pulse text-left">
        {/* Skeleton de Producción Nueva Mensual */}
        <div className="bg-slate-100 p-3.5 rounded-xl space-y-2.5 border border-slate-200/60">
            <div className="flex justify-between items-center">
                <div className="h-3 bg-slate-200 rounded-md w-32" />
                <div className="h-4 bg-slate-200 rounded-md w-16" />
            </div>
            <div className="grid grid-cols-3 gap-2 items-baseline pt-1">
                <div className="col-span-2 space-y-1.5">
                    <div className="h-5 bg-slate-200 rounded-md w-36" />
                    <div className="h-3 bg-slate-200 rounded-md w-24" />
                </div>
                <div className="flex flex-col items-end space-y-1">
                    <div className="h-5 bg-slate-200 rounded-md w-10" />
                    <div className="h-2 bg-slate-200 rounded-md w-16" />
                </div>
            </div>
        </div>

        {/* Skeleton Tabs (Vendedores / Compañías / Riesgos) */}
        <div className="h-7 bg-slate-100 rounded-xl border border-slate-200/60" />

        {/* Skeleton Listado Rankings (Filas) */}
        <div className="space-y-2">
            <div className="h-11 bg-slate-100 rounded-xl border border-slate-200/60" />
            <div className="h-11 bg-slate-100 rounded-xl border border-slate-200/60" />
            <div className="h-11 bg-slate-100 rounded-xl border border-slate-200/60" />
        </div>
    </div>
);

const WidgetProduccionMensual = () => {
    const navigate = useNavigate();
    const [tabActiva, setTabActiva] = useState('vendedores'); // 'vendedores' | 'companias' | 'riesgos'
    const { rangoMesActual, loading, rankings, refetch } = useProduccionMensual();

    // CONDICIÓN DE CARGA INICIAL: Si la consulta está activa y no hay caché previas
    const isInitialLoading = loading && !rankings?.rankingVendedores?.length && rankings?.totalPolizasNetas === 0;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-5 flex flex-col justify-between text-left space-y-4 relative overflow-hidden">
            
            {/* CABECERA DE WIDGET */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100 shrink-0">
                        <span className="text-base">📊</span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-black text-slate-800 text-sm tracking-tight">
                                Producción Mensual
                            </h3>
                            {loading && (
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            {rangoMesActual.labelRango}
                        </p>
                    </div>
                </div>

                <button 
                    onClick={refetch} 
                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    title="Actualizar datos"
                >
                    🔄
                </button>
            </div>

            {/* RENDERIZADO CONDICIONAL: SKELETON O CONTENIDO PRINCIPAL */}
            {isInitialLoading ? (
                <WidgetProduccionSkeleton />
            ) : (
                <div className="space-y-4 animate-fade-in">
                    
                    {/* PRODUCCIÓN MES BRUTA (PÓLIZAS NUEVAS) */}
                    <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-150 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                                Producción Nueva Mensual
                            </span>
                            <span className="text-[10px] font-mono font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                                +{rankings.cantidadEmitidas} nuevas
                            </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2 items-baseline pt-1">
                            <div className="col-span-2">
                                <div className="text-lg font-black text-emerald-600 font-mono leading-none truncate">
                                    {rankings.totalPrimaARSFormatted}
                                </div>
                                <div className="text-[10px] font-bold text-blue-600 font-mono mt-1">
                                    {rankings.totalPrimaUSDFormatted}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-black text-slate-900 font-mono leading-none">
                                    {rankings.totalPolizasNetas}
                                </div>
                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block mt-1">
                                    Pólizas Netas
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* TABS DE RANKING (VENDEDORES / COMPAÑÍAS / RIESGOS) */}
                    <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-center">
                        <button
                            onClick={() => setTabActiva('vendedores')}
                            className={`flex-1 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                                tabActiva === 'vendedores' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            VENDEDORES
                        </button>
                        <button
                            onClick={() => setTabActiva('companias')}
                            className={`flex-1 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                                tabActiva === 'companias' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            COMPAÑÍAS
                        </button>
                        <button
                            onClick={() => setTabActiva('riesgos')}
                            className={`flex-1 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                                tabActiva === 'riesgos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            RIESGOS
                        </button>
                    </div>

                    {/* LISTADO DINÁMICO DE RANKINGS */}
                    <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                        {tabActiva === 'vendedores' && (
                            rankings.rankingVendedores.slice(0, 5).map((item, idx) => (
                                <div key={item.nombre} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                                    <div className="flex items-center gap-2 truncate">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 w-4">{idx + 1}.</span>
                                        <div className="truncate">
                                            <span className="font-bold text-slate-800 block truncate">{item.nombre}</span>
                                            <span className="inline-block mt-0.5 px-2 py-0.5 rounded-md bg-slate-200/60 text-slate-700 font-extrabold text-[10px]">
                                                📄 {item.cantidad} {item.cantidad === 1 ? 'póliza' : 'pólizas'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0 font-mono">
                                        <div className="font-black text-slate-900">${Math.round(item.primaARS).toLocaleString('es-AR')}</div>
                                        {item.primaUSD > 0 && (
                                            <div className="font-bold text-blue-600 text-[10px] mt-0.5">
                                                US$ {Math.round(item.primaUSD).toLocaleString('en-US')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}

                        {tabActiva === 'companias' && (
                            rankings.rankingCompanias.slice(0, 5).map((item, idx) => (
                                <div key={item.nombre} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                                    <div className="flex items-center gap-2 truncate">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 w-4">{idx + 1}.</span>
                                        <div className="truncate">
                                            <span className="font-bold text-slate-800 block truncate">{item.nombre}</span>
                                            <span className="inline-block mt-0.5 px-2 py-0.5 rounded-md bg-slate-200/60 text-slate-700 font-extrabold text-[10px]">
                                                📄 {item.cantidad} {item.cantidad === 1 ? 'póliza' : 'pólizas'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0 font-mono">
                                        <div className="font-black text-slate-900">${Math.round(item.primaARS).toLocaleString('es-AR')}</div>
                                        {item.primaUSD > 0 && (
                                            <div className="font-bold text-blue-600 text-[10px] mt-0.5">
                                                US$ {Math.round(item.primaUSD).toLocaleString('en-US')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}

                        {tabActiva === 'riesgos' && (
                            rankings.rankingRiesgos.slice(0, 5).map((item, idx) => (
                                <div key={item.nombre} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                                    <div className="flex items-center gap-2 truncate">
                                        <span className="text-[10px] font-mono font-bold text-slate-400 w-4">{idx + 1}.</span>
                                        <div className="truncate">
                                            <span className="font-bold text-slate-800 block truncate">{item.nombre}</span>
                                            <span className="inline-block mt-0.5 px-2 py-0.5 rounded-md bg-slate-200/60 text-slate-700 font-extrabold text-[10px]">
                                                📄 {item.cantidad} {item.cantidad === 1 ? 'póliza' : 'pólizas'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0 font-mono">
                                        <div className="font-black text-slate-900">${Math.round(item.primaARS).toLocaleString('es-AR')}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* BOTÓN IR AL REPORTE BI */}
            <button
                onClick={() => navigate('/ReportesHub')}
                className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-xs flex items-center justify-center gap-2 group cursor-pointer"
            >
                <span>Ver Reporte de Pólizas</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default WidgetProduccionMensual;